import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { DataLoaderResult } from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';
import { DashboardSalesComparisonSqlMap } from '../../common/sales/sql-maps/dashboard-sales-comparison-sql-map';

export interface SalesComparisonDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class SalesComparisonDataLoaderFactory extends Factory<
    DataLoader,
    SalesComparisonDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'SalesAnalyticsVsStoreDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: SalesComparisonDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getSalesAnalyticsVsStoreFactory] Provided options are of type SalesAnalyticsVsStoreDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceColumns = queryContext.datasourceColumns;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    DashboardSalesComparisonSqlMap.sqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where, dateTrunc } =
                    DashboardSalesComparisonSqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const query = `
    -- DataLoaderId: ${this.instanceId}

WITH base AS (
  SELECT *
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      DashboardSalesComparisonSqlMap.sqlMap.dataset,
      DashboardSalesComparisonSqlMap.sqlMap.tableId
  )}\`
  ${where}
),

-- cut off beyond latest day for which events data exists
max_event_date AS (
  SELECT MAX(date) AS d FROM base WHERE sales.transactions_cnt IS NOT NULL
),

-- per-store latest date for which report data exists
store_report_complete AS (
  SELECT store, MAX(date) AS report_complete_until
  FROM base
  WHERE report_sales.transactions_cnt IS NOT NULL
  GROUP BY store
),

enriched AS (
  SELECT
    b.*,
    IFNULL(b.date <= src.report_complete_until, FALSE) AS is_report_complete
  FROM base b
  LEFT JOIN store_report_complete src USING(store)
  WHERE b.date <= (SELECT d FROM max_event_date)
)

SELECT ${dateTrunc} AS date,
  ${splitSelect}

  -- cnt
  -- COALESCE so NULL on one side becomes signed contribution from the other.
  -- bucket abs_diff is NULL (not present on chart) whenever any (MIN) contributing store's reports are incomplete
  IF(MIN(is_report_complete),
    SUM(COALESCE(sales.transactions_cnt, 0) - COALESCE(report_sales.transactions_cnt, 0)),
    NULL
  ) AS transactions_cnt_abs_diff,
  SUM(sales.invalid_transactions_cnt) AS invalid_transactions_cnt,

  -- net
  IF(MIN(is_report_complete),
    SUM(COALESCE(sales.revenue_net_pln, 0) - COALESCE(report_sales.revenue_net_pln, 0)),
    NULL
  ) AS revenue_net_pln_abs_diff,
  IF(MIN(is_report_complete),
    SUM(COALESCE(sales.revenue_net_usd, 0) - COALESCE(report_sales.revenue_net_usd, 0)),
    NULL
  ) AS revenue_net_usd_abs_diff,

  -- gross
  IF(MIN(is_report_complete),
    SUM(COALESCE(sales.revenue_gross_pln, 0) - COALESCE(report_sales.revenue_gross_pln, 0)),
    NULL
  ) AS revenue_gross_pln_abs_diff,
  IF(MIN(is_report_complete),
    SUM(COALESCE(sales.revenue_gross_usd, 0) - COALESCE(report_sales.revenue_gross_usd, 0)),
    NULL
  ) AS revenue_gross_usd_abs_diff
FROM enriched
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date
    `;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                var data = [...queryResponse.result];
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
